import { useEffect, useState } from "react";
import { usePortalNavigate } from "../hooks/usePortalNavigate";
import { BadgeCheck } from "lucide-react";
import { theme, card, emptyFrame } from "../theme";
import { StatusTag } from "../components/Drawing";
import { LoadingBlock } from "../components/Spinner";
import { api } from "../api/client";
import { useFrameworks, CE_FRAMEWORK_IDS } from "../context/FrameworksContext";
import { frameworkOfControl, scopeSummaryText } from "../utils/controlRoute";
import { displayControlRef, displayControlTitle } from "../lib/controlRef";

interface GuidanceRun {
  id: string;
  overallScore: number | null;
  resultJson: unknown;
  completedAt: string | null;
  createdAt: string;
}

interface PerControl {
  controlId: string;
  // Framework the worker assessed this control under. Absent on older runs.
  frameworkId?: string;
  status: "COMPLIANT" | "PARTIAL" | "GAP" | "NOT_APPLICABLE";
  evidenceFound: string[];
  gaps: string[];
  recommendations: string[];
}

interface ResultJson {
  overallScore?: number;
  summary?: string;
  summaries?: Record<string, string>;
  perControl?: PerControl[];
  // Frameworks (and pseudo-frameworks such as "legal") this run assessed. Absent on older runs.
  frameworkIds?: string[];
  // Legacy fields from older runs
  keyFindings?: string[];
  findings?: string[];
  recommendations?: string[];
}

const FRAMEWORK_LABELS: Record<string, string> = {
  iso27001:             "ISO 27001:2022",
  iso9001:              "ISO 9001:2015",
  iso42001:             "ISO 42001:2023",
  soc2:                 "SOC 2",
  cyber_essentials:     "Cyber Essentials",
  cyber_essentials_plus:"Cyber Essentials Plus",
  pci_dss:              "PCI DSS v4.0",
  iasme_ca_sole_trader:  "IASME CA (Sole Trader)",
  iasme_ca_micro:        "IASME CA (Micro)",
  iasme_ca_small:        "IASME CA (Small)",
  iasme_ca_medium_large: "IASME CA (Medium/Large)",
  iso45001:             "ISO 45001:2018",
  iso14001:             "ISO 14001:2015",
  ncsc_caf:             "NCSC CAF v4.0",
  legal:                "Legal & Regulatory Obligations",
};

const CE_FAMILY: ReadonlySet<string> = CE_FRAMEWORK_IDS;

/**
 * Which frameworks a run's controls may be shown under: the frameworks the run
 * recorded, otherwise the org's current enrolment. Legal obligations are org-level
 * and always in scope. CE and CE+ share one control set, so either satisfies the other.
 */
function buildScope(result: ResultJson, enrolled: Set<string>) {
  const base = new Set<string>(result.frameworkIds?.length ? result.frameworkIds : enrolled);
  const hasCe = [...base].some((id) => CE_FAMILY.has(id));
  const ceLabelId = base.has("cyber_essentials_plus") ? "cyber_essentials_plus" : "cyber_essentials";
  return {
    // Framework a control belongs to, normalised to the CE tier actually in scope.
    frameworkOf(c: PerControl): string | null {
      const fw = c.frameworkId ?? frameworkOfControl(c.controlId);
      return fw && CE_FAMILY.has(fw) ? ceLabelId : fw;
    },
    inScope(fw: string | null): boolean {
      if (!fw || fw === "legal") return true;
      return base.has(fw) || (CE_FAMILY.has(fw) && hasCe);
    },
    // Headline narrative: per-standard summaries joined when the run recorded them,
    // otherwise the stored narrative with out-of-scope sentences removed.
    summary(): string | null {
      const perFw = Object.entries(result.summaries ?? {}).filter(([fw]) => this.inScope(fw)).map(([, text]) => text);
      if (perFw.length > 0) return perFw.join(" ");
      return result.summary ? scopeSummaryText(result.summary, (fw) => this.inScope(fw)) || null : null;
    },
  };
}

type RunScope = ReturnType<typeof buildScope>;

function scoreColor(score: number) {
  if (score >= 80) return theme.color.successText;
  if (score >= 50) return theme.color.warningText;
  return theme.color.dangerText;
}

function scoreBg(score: number) {
  if (score >= 80) return theme.color.success + "22";
  if (score >= 50) return theme.color.warning + "22";
  return theme.color.danger + "22";
}

function daysAgo(dateStr: string | null): number | null {
  if (!dateStr) return null;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
}

function calcFrameworkScore(controls: PerControl[]): number {
  const applicable = controls.filter((c) => c.status !== "NOT_APPLICABLE");
  if (applicable.length === 0) return 0;
  const compliant = applicable.filter((c) => c.status === "COMPLIANT").length;
  const partial   = applicable.filter((c) => c.status === "PARTIAL").length;
  return Math.round(((compliant + partial * 0.5) / applicable.length) * 100);
}

interface FrameworkBreakdown {
  id: string;
  label: string;
  summary: string | null;
  controls: PerControl[];
  score: number;
  compliant: number;
  partial: number;
  gap: number;
  na: number;
}

function buildFrameworkBreakdowns(result: ResultJson, scope: RunScope): FrameworkBreakdown[] {
  if (!result.perControl?.length) return [];

  // Group controls by framework, dropping anything outside the run's standards
  const grouped: Record<string, PerControl[]> = {};
  for (const c of result.perControl) {
    const fw = scope.frameworkOf(c);
    if (!scope.inScope(fw)) continue;
    (grouped[fw ?? "__other__"] ??= []).push(c);
  }

  const breakdowns: FrameworkBreakdown[] = [];
  for (const [fwId, controls] of Object.entries(grouped)) {
    if (fwId === "__other__") continue;
    breakdowns.push({
      id:       fwId,
      label:    FRAMEWORK_LABELS[fwId] ?? fwId,
      summary:  result.summaries?.[fwId] ?? null,
      controls,
      score:    calcFrameworkScore(controls),
      compliant: controls.filter((c) => c.status === "COMPLIANT").length,
      partial:   controls.filter((c) => c.status === "PARTIAL").length,
      gap:       controls.filter((c) => c.status === "GAP").length,
      na:        controls.filter((c) => c.status === "NOT_APPLICABLE").length,
    });
  }

  // Sort by score ascending (worst frameworks first — most improvement potential)
  return breakdowns.sort((a, b) => a.score - b.score);
}

interface ImprovementItem {
  controlId: string;
  frameworkLabel: string;
  gaps: string[];
  recommendations: string[];
  isAutoFail: boolean;
}

const AUTO_FAIL_KEYS = new Set([
  "um_os_supported", "um_os_patches_14d", "um_app_patches_14d",
  "ua_cloud_admin_mfa", "ua_cloud_user_mfa",
  "A6.1", "A6.4", "A6.5", "A7.16", "A7.17",
]);

function buildImprovementItems(result: ResultJson, scope: RunScope, limit = 10): ImprovementItem[] {
  if (!result.perControl?.length) return [];

  const gaps = result.perControl
    .filter((c) => c.status === "GAP" || c.status === "PARTIAL")
    .filter((c) => c.recommendations?.length > 0)
    .filter((c) => scope.inScope(scope.frameworkOf(c)))
    .map((c): ImprovementItem => ({
      controlId:      c.controlId,
      frameworkLabel: FRAMEWORK_LABELS[scope.frameworkOf(c) ?? ""] ?? "",
      gaps:           c.gaps,
      recommendations: c.recommendations,
      isAutoFail:     AUTO_FAIL_KEYS.has(c.controlId),
    }));

  // Auto-fail first, then remaining gaps
  const autoFails = gaps.filter((i) => i.isAutoFail);
  const rest      = gaps.filter((i) => !i.isAutoFail);

  return [...autoFails, ...rest].slice(0, limit);
}

// ─── Components ──────────────────────────────────────────────────────────────

function ScorePill({ score, size = "md" }: { score: number; size?: "sm" | "md" | "lg" }) {
  const fontSize = size === "lg" ? 40 : size === "md" ? 24 : 16;
  const pad      = size === "lg" ? "14px 22px" : size === "md" ? "8px 16px" : "4px 10px";
  return (
    <div style={{
      display: "inline-flex", flexDirection: "column", alignItems: "center",
      background: scoreBg(score), borderRadius: 3, padding: pad,
    }}>
      <span style={{ fontSize, fontWeight: 800, color: scoreColor(score), lineHeight: 1 }}>
        {score}%
      </span>
      {size === "lg" && (
        <span style={{ fontSize: 11, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 4 }}>
          Overall score
        </span>
      )}
    </div>
  );
}

function StatusBar({ compliant, partial, gap, na }: { compliant: number; partial: number; gap: number; na: number }) {
  const total = compliant + partial + gap + na;
  if (total === 0) return null;
  return (
    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 10 }}>
      {[
        { label: "Compliant", count: compliant, color: theme.color.successText, bg: theme.color.success + "22" },
        { label: "Partial",   count: partial,   color: theme.color.warningText, bg: theme.color.warning + "22" },
        { label: "Gap",       count: gap,        color: theme.color.dangerText,  bg: theme.color.danger  + "22" },
        { label: "N/A",       count: na,         color: theme.color.faint,       bg: theme.color.panelAlt },
      ].map(({ label, count, color, bg }) =>
        count > 0 ? (
          <div key={label} style={{ background: bg, borderRadius: 3, padding: "6px 14px", textAlign: "center", minWidth: 64 }}>
            <div style={{ fontSize: 20, fontWeight: 700, color }}>{count}</div>
            <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 2 }}>{label}</div>
          </div>
        ) : null
      )}
    </div>
  );
}

// ─── Legal gap types ─────────────────────────────────────────────────────────

interface LegalSoaRow {
  control: { id: string; domain: string; name: string; description: string };
  entry: { status: string };
  /** The effective answer: false when the threshold engine or the organisation marked it not applicable. */
  applicable?: boolean;
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function ContinuousGuidancePage() {
  const navigate = usePortalNavigate();
  const { enrolled } = useFrameworks();
  const [run, setRun] = useState<GuidanceRun | null | undefined>(undefined);
  const [legalRows, setLegalRows] = useState<LegalSoaRow[]>([]);

  useEffect(() => {
    api.get<{ run: GuidanceRun | null }>("/guidance/latest").then((d) => setRun(d.run)).catch(() => setRun(null));
    api.get<{ soa: LegalSoaRow[] }>("/legal/soa").then((d) => setLegalRows(d.soa.filter((r) => r.applicable !== false))).catch(() => {});
  }, []);

  if (run === undefined) {
    return <LoadingBlock />;
  }

  const result = run?.resultJson as ResultJson | null | undefined;
  const scope = result ? buildScope(result, enrolled) : null;
  const frameworks  = result && scope ? buildFrameworkBreakdowns(result, scope) : [];
  const improvements = result && scope ? buildImprovementItems(result, scope) : [];
  const hasRichData  = !!result?.perControl?.length;

  const legalGaps = legalRows.filter((r) => r.entry.status === "GAP" || r.entry.status === "NOT_STARTED");
  const legalCompliant = legalRows.filter((r) => r.entry.status === "COMPLIANT").length;
  const legalPct = legalRows.length > 0 ? Math.round((legalCompliant / legalRows.length) * 100) : 0;

  return (
    <div className="page-pad">
      <h1 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>AI advice</h1>
      <p style={{ margin: "0 0 24px", fontSize: 13, color: theme.color.subtle }}>
        AI-generated compliance overview and improvement actions for your organisation.
      </p>

      {/* Legal compliance gaps — shown regardless of AI run status */}
      {legalRows.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading }}>Legal compliance gaps</div>
            <button
              onClick={() => navigate("/legal")}
              style={{ fontSize: 12, color: theme.color.accentText, background: "none", border: "none", cursor: "pointer", fontWeight: 600, padding: 0 }}
            >
              View all obligations →
            </button>
          </div>
          <div style={{ fontSize: 13, color: theme.color.subtle, marginBottom: 12 }}>
            {legalGaps.length === 0
              ? `All ${legalRows.length} obligations across your applicable regulations are compliant.`
              : `${legalGaps.length} of ${legalRows.length} obligations need attention · ${legalPct}% compliant`}
          </div>
          {legalGaps.length === 0 ? (
            <div style={{ ...card, display: "flex", alignItems: "center", gap: 14, padding: "14px 18px" }}>
              <BadgeCheck size={22} color="var(--c-successText)" />
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.successText, marginBottom: 2 }}>All legal obligations met</div>
                <div style={{ fontSize: 12, color: theme.color.subtle }}>
                  {legalCompliant} obligation{legalCompliant !== 1 ? "s" : ""} marked compliant across your applicable regulations.
                </div>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {legalGaps.slice(0, 8).map((row) => {
                const isGap = row.entry.status === "GAP";
                return (
                  <div
                    key={row.control.id}
                    style={{ ...card, borderLeft: `3px solid ${isGap ? theme.color.danger : theme.color.borderStrong}`, padding: "12px 16px", cursor: "pointer" }}
                    onClick={() => navigate("/legal")}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                      <StatusTag tone={isGap ? "gap" : "none"}>{isGap ? "Gap" : "Not started"}</StatusTag>
                      <span style={{ fontSize: 11, color: theme.color.faint }}>{row.control.domain}</span>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{row.control.name}</div>
                    <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 3, lineHeight: 1.5 }}>
                      {row.control.description.length > 120 ? row.control.description.slice(0, 120) + "…" : row.control.description}
                    </div>
                  </div>
                );
              })}
              {legalGaps.length > 8 && (
                <button
                  onClick={() => navigate("/legal")}
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: theme.color.accentText, fontWeight: 600, textAlign: "left", padding: "4px 0" }}
                >
                  +{legalGaps.length - 8} more — View all legal obligations →
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {!run ? (
        <div style={{ ...emptyFrame, padding: "48px 20px" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: theme.color.heading, marginBottom: 8 }}>No guidance available yet</div>
          <div style={{ fontSize: 13, color: theme.color.subtle, marginBottom: 20 }}>
            Run a compliance check from 'Check My Compliance' to generate guidance here.
          </div>
          <button
            onClick={() => navigate("/compliance")}
            style={{ padding: "9px 18px", fontSize: 13, fontWeight: 600, background: theme.color.accent, color: "#fff", borderRadius: 3, border: "none", cursor: "pointer" }}
          >
            Go to Check My Compliance →
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          {/* Staleness banner */}
          {(() => {
            const age = daysAgo(run.completedAt);
            if (age === null || age < 30) return null;
            return (
              <div style={{ ...card, border: `1px solid ${theme.color.warning}`, background: theme.color.warning + "18", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div style={{ fontSize: 13, color: theme.color.warningText }}>
                  This guidance is {age} days old. Run a new compliance check to refresh your score and recommendations.
                </div>
                <button
                  onClick={() => navigate("/compliance")}
                  style={{ flexShrink: 0, padding: "6px 14px", fontSize: 12, fontWeight: 600, background: theme.color.accent, color: "#fff", border: "none", borderRadius: 3, cursor: "pointer" }}
                >
                  Run new check
                </button>
              </div>
            );
          })()}

          {/* Score header */}
          <div style={{ ...card, display: "flex", alignItems: "center", gap: 24 }}>
            {run.overallScore != null && <ScorePill score={run.overallScore} size="lg" />}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>
                Latest compliance check results
              </div>
              <div style={{ fontSize: 12, color: theme.color.subtle }}>
                Last updated: {run.completedAt ? new Date(run.completedAt).toLocaleString() : new Date(run.createdAt).toLocaleString()}
              </div>
              {scope?.summary() && (
                <div style={{ fontSize: 13, color: theme.color.text, lineHeight: 1.7, marginTop: 10 }}>
                  {scope.summary()}
                </div>
              )}
            </div>
            <button
              onClick={() => navigate("/compliance")}
              style={{ flexShrink: 0, padding: "7px 14px", fontSize: 12, fontWeight: 600, background: "var(--c-accentBg)", color: "var(--c-accentText)", border: `1px solid ${theme.color.accent}`, borderRadius: 3, cursor: "pointer" }}
            >
              Run new check
            </button>
          </div>

          {/* Per-framework breakdown */}
          {frameworks.length > 0 && (
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 12 }}>
                Framework overview
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {frameworks.map((fw) => (
                  <div key={fw.id} style={{ ...card, display: "flex", gap: 20, alignItems: "flex-start" }}>
                    <div style={{ flexShrink: 0, paddingTop: 2 }}>
                      <ScorePill score={fw.score} size="md" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 6 }}>
                        {fw.label}
                      </div>
                      {fw.summary && (
                        <div style={{ fontSize: 13, color: theme.color.text, lineHeight: 1.7 }}>
                          {fw.summary}
                        </div>
                      )}
                      <StatusBar compliant={fw.compliant} partial={fw.partial} gap={fw.gap} na={fw.na} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Improvement actions */}
          {improvements.length > 0 && (
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>
                How to improve your score
              </div>
              <div style={{ fontSize: 13, color: theme.color.subtle, marginBottom: 12 }}>
                Priority actions derived from your compliance gaps — auto-fail controls are listed first.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {improvements.map((item) => (
                  <div
                    key={item.controlId}
                    style={{
                      ...card,
                      borderLeft: `3px solid ${item.isAutoFail ? theme.color.danger : theme.color.warning}`,
                      padding: "14px 18px",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      {item.isAutoFail && (
                        <span style={{ fontSize: 10, fontWeight: 700, background: theme.color.danger + "33", color: theme.color.dangerText, borderRadius: 4, padding: "2px 6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          Auto-fail
                        </span>
                      )}
                      <span style={{ fontSize: 12, fontWeight: 700, color: theme.color.heading }}>
                        {[displayControlRef(item.controlId), displayControlTitle(item.controlId, "")].filter(Boolean).join(" ")}
                      </span>
                      {item.frameworkLabel && (
                        <span style={{ fontSize: 11, color: theme.color.faint }}>— {item.frameworkLabel}</span>
                      )}
                    </div>
                    {item.gaps.length > 0 && (
                      <div style={{ fontSize: 12, color: theme.color.dangerText, marginBottom: 6 }}>
                        Gap: {item.gaps.join("; ")}
                      </div>
                    )}
                    <ul style={{ margin: 0, paddingLeft: 16, display: "flex", flexDirection: "column", gap: 4 }}>
                      {item.recommendations.map((r, i) => (
                        <li key={i} style={{ fontSize: 13, color: theme.color.text, lineHeight: 1.6 }}>{r}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fallback: legacy fields if no rich perControl data */}
          {!hasRichData && (
            <>
              {(result?.keyFindings?.length || result?.findings?.length) ? (
                <div style={card}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 10 }}>Key Findings</div>
                  <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 6 }}>
                    {(result?.keyFindings || result?.findings || []).map((f, i) => (
                      <li key={i} style={{ fontSize: 13, color: theme.color.text, lineHeight: 1.6 }}>{f}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {result?.recommendations?.length ? (
                <div style={card}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 10 }}>Recommendations</div>
                  <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 6 }}>
                    {result.recommendations.map((r, i) => (
                      <li key={i} style={{ fontSize: 13, color: theme.color.text, lineHeight: 1.6 }}>{r}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </>
          )}

        </div>
      )}
    </div>
  );
}
